import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ToastProvider } from './components/ui';
import { Root } from './components/Root';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Profile } from './pages/profile/Profile';
import { ProfileSetup } from './pages/profile/ProfileSetup';
import { InterviewersList } from './pages/interviewers/InterviewersList';
import { TimeSlotManagement } from './pages/interviewers/TimeSlotManagement';
import { Bookings } from './pages/bookings/Bookings';
import { PrivateRoute } from './components/PrivateRoute';
import { RoleBasedRoute } from './components/RoleBasedRoute';
import { UserRole } from './types/user';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { BecomeInterviewer } from './pages/BecomeInterviewer';
import { AuthProvider } from './contexts/AuthContext';
import { PaymentSuccess } from './pages/payment/PaymentSuccess';
import { InterviewTraining } from './pages/interview/InterviewTraining';
import { TryInterview } from './pages/interview/TryInterview';
import { AdminPanel } from './pages/admin/AdminPanel';
import { PrivacyPolicy } from './pages/legal/PrivacyPolicy';
import { TermsOfService } from './pages/legal/TermsOfService';
import { PaymentCancel } from './pages/payment/PaymentCancel';
import { NotFound } from './pages/NotFound';
import { UiPlayground } from './pages/dev/UiPlayground';

import { analytics } from './services/analytics';
import { currencyService } from './services/currency';

/**
 * Main App component that sets up the router and defines all routes.
 */
function App() {
  // Initialize services on app startup
  useEffect(() => {
    analytics.initialize();
    currencyService.initialize();
  }, []);

  const isDev = process.env.NODE_ENV === 'development';

  return (
    <ToastProvider>
      <AuthProvider>
        <Router>
          <Routes>
            {isDev && (
              <Route path="/_ui" element={<UiPlayground />} />
            )}
            <Route path="/" element={<Root />}>
            {/* Public Routes */}
            <Route index element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/become-interviewer" element={<BecomeInterviewer />} />
            <Route path="/try-interview" element={<TryInterview />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            
            <Route path="/login" element={<Login />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />

            <Route
              path="/register"
              element={<Register />}
            />
            
            {/* Protected Routes - Require Authentication */}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <div className="px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto my-12 max-w-4xl rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card sm:p-8 dark:shadow-none">
                      <Profile />
                    </div>
                  </div>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/profile/setup"
              element={
                <PrivateRoute>
                  <div className="px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto my-12 max-w-4xl rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card sm:p-8 dark:shadow-none">
                      <ProfileSetup />
                    </div>
                  </div>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/interviewers"
              element={
                <PrivateRoute>
                  <InterviewersList />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/bookings"
              element={
                <PrivateRoute>
                  <div className="px-4 sm:px-6 lg:px-8">
                    <div className="mx-auto my-12 max-w-7xl rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card sm:p-8 dark:shadow-none">
                      <Bookings />
                    </div>
                  </div>
                </PrivateRoute>
              }
            />

            <Route
              path="/interviewee-bookings"
              element={<Navigate to="/bookings" replace />}
            />
            <Route
              path="/interviewer-bookings"
              element={<Navigate to="/bookings" replace />}
            />

            <Route
              path="/time-slots"
              element={
                <RoleBasedRoute allowedRoles={[UserRole.Interviewer]}>
                  <TimeSlotManagement />
                </RoleBasedRoute>
              }
            />

            {/* AI Interview Training Routes */}
            <Route
              path="/interview-training"
              element={
                <PrivateRoute>
                  <InterviewTraining />
                </PrivateRoute>
              }
            />

            {/* Admin Panel - Protected Route */}
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <div className="min-h-screen">
                    <AdminPanel />
                  </div>
                </PrivateRoute>
              }
            />

            {/* Payment Routes - Public (accessible without authentication) */}
            <Route
              path="/payment/result"
              element={<PaymentSuccess />}
            />
            <Route
              path="/payment/success"
              element={<PaymentSuccess />}
            />
            <Route
              path="/payment/callback"
              element={<PaymentSuccess />}
            />
            <Route path="/payment/cancel" element={<PaymentCancel />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Router>
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
